// web/src/lib/auth.ts
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  onIdTokenChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  getIdToken as fbGetIdToken,
  getIdTokenResult,
  reload,
  signOut as fbSignOut,
  type IdTokenResult,
  type User,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { createElement } from "react";

/** ----------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------- */
export type AuthStatus = "loading" | "signedOut" | "signedIn";

export type AuthContextValue = {
  /** Overall status: use to gate UI. */
  status: AuthStatus;
  /** Firebase user object when signed in (null otherwise). */
  user: User | null;
  /** Convenience: current uid or null. */
  uid: string | null;

  /** Cached custom claims (if any). */
  claims: IdTokenResult["claims"] | null;

  /** "Remember me" preference (local vs. session persistence). */
  remember: boolean;

  /** Toggle persistence and store preference. */
  setRemember: (remember: boolean) => Promise<void>;

  /** Force-refresh the Firebase user (provider data, photoURL, etc.). */
  refreshUser: () => Promise<void>;

  /** Get a (possibly fresh) ID token string; returns null if signed out. */
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;

  /** Sign out and clear state. */
  signOut: () => Promise<void>;
};

/** ----------------------------------------------------------------
 * Persistence preference (Remember me)
 * ---------------------------------------------------------------- */
const REMEMBER_KEY = "sg_auth_remember";
const DEFAULT_REMEMBER = true;

function readRemember(): boolean {
  try {
    const v = localStorage.getItem(REMEMBER_KEY);
    if (v == null) return DEFAULT_REMEMBER;
    return v === "1" || v === "true";
  } catch {
    return DEFAULT_REMEMBER;
  }
}
function writeRemember(v: boolean) {
  try {
    localStorage.setItem(REMEMBER_KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/** ----------------------------------------------------------------
 * Ready signal (optional helper for modules that need it)
 * ---------------------------------------------------------------- */
let _resolveReady: (() => void) | null = null;
const _readyPromise = new Promise<void>((res) => {
  _resolveReady = res;
});
/** Await this if you must ensure the initial auth check has completed. */
export function authReady(): Promise<void> {
  return _readyPromise;
}

/** ----------------------------------------------------------------
 * Context
 * ---------------------------------------------------------------- */
const AuthCtx = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

/** ----------------------------------------------------------------
 * Provider
 * ---------------------------------------------------------------- */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(auth.currentUser ?? null);
  const [claims, setClaims] = useState<IdTokenResult["claims"] | null>(null);
  const [remember, _setRemember] = useState<boolean>(readRemember());

  const initialResolved = useRef(false);

  // Ensure persistence matches the saved preference.
  useEffect(() => {
    const p = remember ? browserLocalPersistence : browserSessionPersistence;
    // affects subsequent sign-ins
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setPersistence(auth, p).catch(() => void 0);
  }, [remember]);

  // Auth state + token/claims subscriptions
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setStatus(u ? "signedIn" : "signedOut");

      if (u) {
        try {
          const res = await getIdTokenResult(u, /*forceRefresh*/ false);
          setClaims(res.claims ?? null);
        } catch {
          setClaims(null);
        }
      } else {
        setClaims(null);
      }

      if (!initialResolved.current) {
        initialResolved.current = true;
        if (_resolveReady) _resolveReady();
      }
    });

    const unsubToken = onIdTokenChanged(auth, async (u) => {
      if (!u) {
        setClaims(null);
        return;
      }
      try {
        const res = await getIdTokenResult(u, /*forceRefresh*/ false);
        setClaims(res.claims ?? null);
      } catch {
        /* ignore */
      }
    });

    return () => {
      unsubAuth();
      unsubToken();
    };
  }, []);

  const setRemember = useCallback(async (next: boolean) => {
    _setRemember(next);
    writeRemember(next);
    const p = next ? browserLocalPersistence : browserSessionPersistence;
    await setPersistence(auth, p).catch(() => void 0);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      await reload(auth.currentUser);
      const res = await getIdTokenResult(auth.currentUser, true);
      setClaims(res.claims ?? null);
      setUser(auth.currentUser);
      setStatus("signedIn");
    } catch {
      /* ignore */
    }
  }, []);

  const getIdToken = useCallback(async (forceRefresh = false) => {
    const u = auth.currentUser;
    if (!u) return null;
    try {
      const token = await fbGetIdToken(u, forceRefresh);
      return token;
    } catch {
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth).catch(() => void 0);
    setUser(null);
    setClaims(null);
    setStatus("signedOut");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      uid: user?.uid ?? null,
      claims,
      remember,
      setRemember,
      refreshUser,
      getIdToken,
      signOut,
    }),
    [status, user, claims, remember, setRemember, refreshUser, getIdToken, signOut],
  );

  return createElement(AuthCtx.Provider, { value }, children as ReactNode);
}

/** ----------------------------------------------------------------
 * Convenience helpers (optional)
 * ---------------------------------------------------------------- */

/** Throws if not signed in; useful inside event handlers. */
export function assertSignedIn(): asserts auth is { currentUser: User } {
  if (!auth.currentUser) {
    throw new Error("You must be signed in to perform this action.");
  }
}

/** Quick boolean accessor (avoids importing the hook in non-React code). */
export function isSignedInNow(): boolean {
  return !!auth.currentUser;
}

